import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';

const STATUS_OPTIONS = ['queued', 'processing', 'completed', 'failed', 'cancelled'];

function age(ts) {
  if (!ts) return '—';
  const ms = Date.now() - new Date(ts).getTime();
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  return `${Math.round(ms / 3_600_000)}h`;
}

export default function BackgroundJobs() {
  const [jobs, setJobs] = useState([]);
  const [statuses, setStatuses] = useState(new Set(['queued', 'processing']));
  const [jobType, setJobType] = useState('');
  const [caseFilter, setCaseFilter] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The route takes ONE status; several ticked boxes = one request each
      // (sending "queued,processing" was a 400 and the page showed nothing).
      const wanted = [...statuses];
      const pages = await Promise.all(
        (wanted.length ? wanted : [null]).map((st) => {
          const params = new URLSearchParams();
          if (st) params.set('status', st);
          params.set('limit', '100');
          return api.get(`/ops_admin/background-jobs?${params.toString()}`);
        }),
      );
      const seen = new Set();
      let items = [];
      for (const d of pages) {
        for (const j of (d?.items || d || [])) {
          if (j?.id && !seen.has(j.id)) { seen.add(j.id); items.push(j); }
        }
      }
      items.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
      if (jobType) items = items.filter(j => j.job_type?.includes(jobType));
      if (caseFilter) items = items.filter(j => j.case_id?.startsWith(caseFilter));
      setJobs(items);
    } catch (e) {
      // say what threw and where: a bare browser message ("The string did not match the
      // expected pattern") told nobody anything on 2026-10-15
      const where = String(e?.stack || '').split('\n').slice(0, 2).join(' ← ');
      setError(`${e?.name || 'Error'}: ${e?.message || 'fetch failed'}${e?.status ? ` (HTTP ${e.status})` : ''}${where ? ` — ${where}` : ''}`);
    } finally {
      setLoading(false);
    }
  }, [statuses, jobType, caseFilter]);

  useEffect(() => {
    fetchJobs();
    const t = setInterval(fetchJobs, 15000);
    return () => clearInterval(t);
  }, [fetchJobs]);

  const cancelJob = async (jobId) => {
    setBusy(b => ({ ...b, [jobId]: 'cancelling' }));
    try {
      await api.post(`/ops_admin/background-jobs/${jobId}/cancel`);
      await fetchJobs();
    } catch (e) {
      setError(`Cancel failed for ${jobId}: ${e.message}`);
    } finally {
      setBusy(b => { const n = { ...b }; delete n[jobId]; return n; });
    }
  };

  const cancelAllVisible = async () => {
    const cancellable = jobs.filter(j => ['queued', 'processing'].includes(j.status));
    if (!cancellable.length) return;
    if (!window.confirm(`Cancel ${cancellable.length} jobs?`)) return;
    for (const j of cancellable) {
      await cancelJob(j.id);
    }
  };

  const toggleStatus = (s) => {
    setStatuses(prev => {
      const n = new Set(prev);
      if (n.has(s)) n.delete(s); else n.add(s);
      return n;
    });
  };

  return (
    <div className="ops-bg-jobs">
      <h2>Background Jobs</h2>

      <div className="ops-bg-jobs-filters" style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <strong>Status:</strong>{' '}
          {STATUS_OPTIONS.map(s => (
            <label key={s} style={{ marginRight: 8 }}>
              <input
                type="checkbox"
                checked={statuses.has(s)}
                onChange={() => toggleStatus(s)}
              /> {s}
            </label>
          ))}
        </div>
        <div>
          <input
            type="text"
            placeholder="job_type contains…"
            value={jobType}
            onChange={e => setJobType(e.target.value)}
            style={{ padding: '4px 8px' }}
          />
        </div>
        <div>
          <input
            type="text"
            placeholder="case_id starts with…"
            value={caseFilter}
            onChange={e => setCaseFilter(e.target.value)}
            style={{ padding: '4px 8px', width: 220 }}
          />
        </div>
        <button onClick={fetchJobs} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        {jobs.some(j => ['queued', 'processing'].includes(j.status)) && (
          <button onClick={cancelAllVisible} style={{ background: '#dc2626', color: 'white' }}>
            Cancel all visible
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: '#fee', padding: 8, marginBottom: 12, color: '#900' }}>
          {error}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
            <th style={{ padding: 6 }}>Status</th>
            <th style={{ padding: 6 }}>Type</th>
            <th style={{ padding: 6 }}>Case</th>
            <th style={{ padding: 6 }}>Created</th>
            <th style={{ padding: 6 }}>Started</th>
            <th style={{ padding: 6 }}>By</th>
            <th style={{ padding: 6 }}></th>
          </tr>
        </thead>
        <tbody>
          {jobs.length === 0 && (
            <tr><td colSpan={7} style={{ padding: 12, color: '#666' }}>No jobs match.</td></tr>
          )}
          {jobs.map(j => (
            <tr key={j.id} style={{ borderTop: '1px solid #eee' }}>
              <td style={{ padding: 6 }}>
                <span style={{
                  padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                  background: j.status === 'processing' ? '#fef3c7'
                            : j.status === 'queued' ? '#dbeafe'
                            : j.status === 'completed' ? '#d1fae5'
                            : j.status === 'failed' ? '#fee2e2'
                            : '#e5e7eb',
                }}>
                  {j.status}
                </span>
              </td>
              <td style={{ padding: 6 }}>{j.job_type}</td>
              <td style={{ padding: 6, fontFamily: 'monospace', fontSize: 11 }}>
                {j.case_id ? j.case_id.slice(0, 8) + '…' : '—'}
              </td>
              <td style={{ padding: 6 }}>{age(j.created_at)} ago</td>
              <td style={{ padding: 6 }}>{j.started_at ? age(j.started_at) + ' ago' : '—'}</td>
              <td style={{ padding: 6, fontSize: 11 }}>{j.created_by || '—'}</td>
              <td style={{ padding: 6 }}>
                {['queued', 'processing'].includes(j.status) && (
                  <button
                    onClick={() => cancelJob(j.id)}
                    disabled={busy[j.id]}
                  >
                    {busy[j.id] === 'cancelling' ? '…' : 'Cancel'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
